import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import * as Yup from 'yup';
import { MaterialAutocomplete } from '../../common/formik-material-ui';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
// remove csv download for now
// import { CSVLink } from 'react-csv';
// import moment from 'moment';
// import GridOnIcon from '@mui/icons-material/GridOn';
// import { startingDate } from '../../recoil/atoms/startingDate';
// import { endingDate } from '../../recoil/atoms/endingDate';
import InfoIcon from '@mui/icons-material/Info';
import BarChartIcon from '@mui/icons-material/PollOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import Highcharts from 'highcharts';
import cookie from 'react-cookies';
import { useMutation } from 'react-query';
import Axios from '../../common/axiosInterceptor';
import { updateBusinessID } from '../../common/changeBusinessID';
import { Logger } from '../../common/Logger';
import { PopoverGeneric } from '../../common/PopoverGeneric';
import { getApiMutation } from '../../services/rest/getApiMutation';
import { HighchartsReactWithStyles } from '../components/charts/HighchartsReactWithStyles';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';

import { AxiosRequestConfig } from 'axios';

const useStyles = makeStyles((theme: Theme) => ({
  barSkeleton: {},
  dropdown: {
    marginLeft: '1vw',
  },
  button: {
    marginLeft: '1vw',
    marginTop: 8,
  },
  autoField: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '190px',
  },
  toolbarItem: {
    marginRight: '5px',
    marginTop: '5px',
    marginBottom: 0,
  },
  toolbarButton: {
    paddingTop: '7px',
    paddingBottom: '7px',
  },
  statItemContainer: {
    //overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '100px',
    textAlign: 'center',
  },
  adminBolded: {
    fontWeight: 'bold',
  },
}));

const BIDSearch = () => {
  const classes = useStyles();
  const [_userAtom, setUserAtom] = useRecoilState(user);
  const userAtom = _userAtom as IUserAtom;
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedBid, setSelectedBid] = useState<number | null>(null);
  const [sortModel, _setSortModel] = useState<GridSortModel>([
    { field: 'businessid', sort: 'asc' },
  ]);
  const [values, setValues] = useState<{
    type?: string;
    value?: string;
    autoSwitch?: boolean;
  }>({});
  const [users, setUsers] = useState<
    { fname: string; lname: string; userid?: number; email?: string }[]
  >([]);
  const [accountOwner, setAccountOwner] = useState<{
    fname: string;
    lname: string;
    userid?: number;
    email?: string;
  }>({
    fname: '',
    lname: '',
    email: '',
  });
  const originalBusinessObjectString = localStorage.getItem(
    'originalBusinessObject'
  );
  const originalBusinessObject = originalBusinessObjectString
    ? (JSON.parse(originalBusinessObjectString) as {
        apikey: string;
        businessid: number;
      })
    : undefined;

  // const [start] = useRecoilState(startingDate);
  // const [end] = useRecoilState(endingDate);

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const mutation = useMutation(
    getApiMutation(userAtom as IUserAtom, setUserAtom)
  );

  const [
    _onboardingCache,
    onboardingWalkthroughLoadable,
    refreshToggleOnboardingWalkthrough,
  ] = makeApiRequestCached(
    {
      method: 'POST',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_OW,
      payloadJsonString: JSON.stringify({
        apikey: userAtom.apikey ?? userAtom.business?.apiKey,
        apiKey: userAtom.apikey ?? userAtom.business?.apiKey,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  React.useEffect(() => {
    const getBusinessUsers = async () => {
      const accessToken = cookie.load('accessToken') || userAtom.accessToken; // use old access token
      try {
        const request: AxiosRequestConfig = {
          method: 'post',
          url: `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/legacy/getBusinessUsers`,
          data: {
            call: methodNameEnum.GET_BUSINESS_USERS,
            apikey:
              userAtom.businessid !== 995
                ? originalBusinessObject?.apikey
                : userAtom.apikey,
            apiKey:
              userAtom.businessid !== 995
                ? originalBusinessObject?.apikey
                : userAtom.apikey,
            businessid: 995,
            businessId: 995,
          },
          headers: {
            Authorization: accessToken, // accessToken in recoil maybe stale
            'x-api-key': userAtom.apikey,
            'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
          },
        };
        const response = await Axios(request);
        Logger.info(
          {
            pathname: window.location.pathname,
            businessid: 995,
            apiKey:
              userAtom.businessid !== 995
                ? originalBusinessObject?.apikey
                : userAtom.apikey,
            userid: userAtom.user?.userid,
            endpoint: 'legacy/getBusinessSegmentESData',
            request,
            // response,
          },
          'BIDSearch'
        );
        if (response?.data && response.status === 200) {
          setUsers(response?.data?.result ?? []);
        }
      } catch (err) {
        Logger.error(
          {
            message: 'BID SEARCH loading business Users',
            err,
          },
          'BIDSearch'
        );
      }
    };
    getBusinessUsers();
  }, []);

  React.useEffect(() => {
    if (onboardingWalkthroughLoadable?.state === 'hasValue') {
      const updatedOW =
        onboardingWalkthroughLoadable?.contents?.response?.data?.data;
      updatedOW?.displayOnboarding !==
        (userAtom as IUserAtom | undefined)?.onboardingWalkthrough
          ?.displayOnboarding &&
        setUserAtom({
          ...userAtom,
          onboardingWalkthrough: updatedOW,
        });
    }
  }, [onboardingWalkthroughLoadable, userAtom]);

  const [
    _businessSegmentDataCached,
    businessSegmentDataLoadable,
    setRefetchToggleForBusinessSegment,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_SEGMENT,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_SEGMENT,
        apikey:
          originalBusinessObject?.apikey ??
          userAtom.apikey ??
          userAtom.business?.apiKey,
        apiKey:
          originalBusinessObject?.apikey ??
          userAtom.apikey ??
          userAtom.business?.apiKey,
        businessid: originalBusinessObject?.businessid ?? userAtom.businessid,
        businessId: originalBusinessObject?.businessid ?? userAtom.businessid,
        startrow:
          (page - 1) * pageSize + 1 === 1 ? 0 : (page - 1) * pageSize + 1,
        batchlength: pageSize,
        type: values?.type ?? '',
        value: values?.value ?? '',
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const bids = useMemo(
    () =>
      businessSegmentDataLoadable.contents?.response?.data?.result?.length > 0
        ? businessSegmentDataLoadable.contents?.response?.data?.result?.map(
            (item) => item.businessid
          )
        : [],
    [businessSegmentDataLoadable]
  );
  const [
    _businessSegmentEsDataCached,
    businessSegmentEsDataLoadable,
    _setRefetchToggleForBsEsData,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_SEGMENT_ES_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_SEGMENT_ES_DATA,
        apikey:
          originalBusinessObject?.apikey ??
          userAtom.apikey ??
          userAtom.business?.apiKey,
        apiKey:
          originalBusinessObject?.apikey ??
          userAtom.apikey ??
          userAtom.business?.apiKey,
        businessid: originalBusinessObject?.businessid ?? userAtom.businessid,
        businessId: originalBusinessObject?.businessid ?? userAtom.businessid,
        bIds: bids,
      }),
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );

  const eSSegTransformed = useMemo(
    () =>
      businessSegmentEsDataLoadable?.state === 'hasValue'
        ? businessSegmentEsDataLoadable?.contents?.response?.data
        : [],
    [businessSegmentEsDataLoadable]
  );

  const formatMoney = (amount: number) => {
    if (!amount) {
      return '$0';
    }
    const newAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
    return newAmount.slice(0, -3); //remove the last three chars
  };

  React.useEffect(() => {
    setRefetchToggleForBusinessSegment({});
  }, [page, pageSize]);

  const crmNames = {
    0: 'None',
    3: 'PipeDrive (1)',
    4: 'Salesforce',
    13: 'Zoho',
    16: 'Hubspot',
    17: 'Piperive 2',
  } as const;

  const getCRMName = (crmid: number) => {
    return crmNames[crmid] ?? `CRM ID ${crmid}`;
  };

  const [__, usageReportDataLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_USAGE_REPORTS_BY_BUSINESSID,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_USAGE_REPORTS_BY_BUSINESSID,
        size: 20,
        bid: selectedBid,
        //transactionid: Date.now(),
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const usageReportData = useMemo(
    () =>
      usageReportDataLoadable?.state === 'hasValue' &&
      usageReportDataLoadable.contents?.response?.data?.result?.hits?.hits,
    [usageReportDataLoadable]
  );

  const usageReportChartOptions = useMemo<
    Highcharts.Options | undefined
  >(() => {
    if (usageReportDataLoadable?.state === 'hasValue') {
      const usageReprotSeriesData: any[] = [];
      const apiLevelSeriesData: any[] = [];
      for (let i = usageReportData?.length - 1, theDate; i >= 0; i--) {
        theDate = Date.parse(usageReportData[i]?._source?.createdDate);
        usageReprotSeriesData.push([
          theDate,
          usageReportData[i]?._source?.usedRequests,
        ]);
        apiLevelSeriesData.push([
          theDate,
          usageReportData[i]?._source?.quotaLimit,
        ]);
      }
      return {
        title: {
          text: 'Usage Chart Report',
        },
        subtitle: {
          text: 'Last 20 data points where usage was above zero',
        },
        xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
            // don't display the year
            month: '%e. %b',
            year: '%b',
          },
          labels: {
            formatter: function () {
              return new Date(this.value).toLocaleDateString('en-us', {
                timeZone: 'UTC',
                month: 'numeric',
                day: 'numeric',
              });
              // return Highcharts.dateFormat('%m/%e', this.value);
            },
          },
          title: {
            text: 'Date',
          },
        },
        yAxis: [
          {
            title: {
              text: 'API Call Count',
              style: { color: '#6CF' },
            },
            min: 0,
            labels: {
              style: { color: '#6CF' },
            },
          },
          {
            title: {
              text: 'API Level',
              style: { color: 'red' },
            },
            min: 0,
            labels: {
              style: { color: 'red' },
            },
            opposite: true,
          },
        ],
        tooltip: {
          headerFormat: '{point.x:%b %e, %Y}<br/>',
          pointFormat: '{series.name}: <b>{point.y:.0f}</b>',
        },
        plotOptions: {
          series: {
            marker: {
              enabled: true,
              radius: 2.5,
            },
          },
        },
        colors: ['#6CF', 'red'],
        series: [
          {
            type: 'column',
            name: 'API Call Count',
            yAxis: 0,
            data: usageReprotSeriesData,
          },
          {
            type: 'line',
            name: 'API Level',
            yAxis: 1,
            data: apiLevelSeriesData,
          },
        ],
        credits: {
          enabled: false,
        },
        responsive: {
          rules: [
            {
              condition: {
                minWidth: 500,
              },
            },
          ],
        },
      };
    }
  }, [usageReportDataLoadable, usageReportData]);
  const get10DlcBrandStatusMessaging = async (bid, apikey) => {
    try {
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.GET_10DLC_STATUS_MESSAGE,
        payload: {
          apikey: apikey,
          call: methodNameEnum.GET_10DLC_STATUS_MESSAGE,
          businessid: bid,
        },
      });
      if (response?.data?.success && response?.data?.result?.[0]) {
        const updatedBrandStatus = {
          brandStatus: response?.data?.result?.[0]?.status ?? '',
          businessNumbersCount: response?.data?.result?.[0]?.count ?? 0,
        };
        return updatedBrandStatus;
      } else {
        enqueueSnackbarWithStyles({
          message: response?.data?.message,
          alertProps: {
            severity: 'error',
          },
        });
        return {
          brandStatus: '',
          businessNumbersCount: 0,
        };
      }
    } catch (error) {
      return {
        brandStatus: '',
        businessNumbersCount: 0,
      };
    }
  };

  const handleSwitch = useCallback((id, apikey, updatedBrandStatus) => {
    const res = updateBusinessID(id, apikey);
    if (res.success) {
      res.user = { ...res.user, brandStatus10Dlc: updatedBrandStatus };
      setUserAtom(res.user);
      //SuperAdminStats.showSuperAdminStats();
    } else {
      enqueueSnackbarWithStyles({
        message: res.error,
        alertProps: {
          severity: 'error',
        },
      });
    }
  }, []);

  const onHandleSwitch = async (bid, apikey) => {
    refreshToggleOnboardingWalkthrough({});
    const updatedStatusMessage = await get10DlcBrandStatusMessaging(
      bid,
      apikey
    );
    handleSwitch(bid, apikey, updatedStatusMessage);
  };
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Switch Business',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>
            <IconButton
              aria-label="add"
              color="primary"
              onClick={() => {
                onHandleSwitch(params.row.id, params.row.apiKey);
              }}
              size="large"
            >
              <SyncAltIcon />
            </IconButton>
          </span>
        ),
      },
      {
        field: 'name',
        headerName: 'Business Name',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'businessid',
        headerName: 'Business ID',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'prepaidTrial',
        headerName: 'Paid Status',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span style={{ fontWeight: 'bold' }}>
            <Tooltip title={'Manage Account'}>
              <NavLink
                to="/manage/account"
                style={{
                  color: `${params.row.prepaidTrial ? 'red' : 'green'}`,
                }}
              >
                {params.value ? 'On Trial' : 'Paid'}
              </NavLink>
            </Tooltip>
          </span>
        ),
      },
      {
        field: 'PaidMonthsCt',
        headerName: 'Account Length',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'AvgMRR',
        headerName: 'Avg MRR',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'MaxMRR',
        headerName: 'Max MRR',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'AvgChange',
        headerName: 'Avg Monthly Change',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'MaxChange',
        headerName: 'Max Monthly Change',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'businessTimezone',
        headerName: 'Region',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'revPerActiveUser',
        headerName: 'Revenue Per Active User',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'numActiveUsers',
        headerName: 'Active Agents (seats)',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'freeAgents',
        headerName: 'Free Users',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'basicphoneunlimited',
        headerName: 'Basic Users',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'integratedphoneunlimited',
        headerName: 'Professional Users',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'enterprisephoneunlimited',
        headerName: 'Enterprise Users',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'EndingMRR',
        headerName: 'Billing',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span style={{ fontWeight: 'bold' }}>
            {/* <Tooltip title={'Billing'}>
              <NavLink
                to={{
                  pathname: '/billing/subscription',
                }}
              >
                ({formatMoney(Number(params.value ?? 0))})
              </NavLink>
            </Tooltip>{' '} */}
            <span>({formatMoney(Number(params.value ?? 0))})</span> -{' '}
            {params.row.cf_StripeCustomerID && (
              <a
                href={`https://dashboard.stripe.com/customers/${params.row.cf_StripeCustomerID}`}
                target={'_blank'}
                rel="noreferrer"
              >
                <Tooltip title={'Stripe'}>
                  <CreditCardOutlinedIcon />
                </Tooltip>
              </a>
            )}
          </span>
        ),
      },
      {
        field: 'invoicedUntil',
        headerName: 'Invoiced Until',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'billDay',
        headerName: 'Bill Date',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'reserveBalance',
        headerName: 'Reserve $',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'minimumBalance',
        headerName: 'Min Balance',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'rechargeAmount',
        headerName: 'Recharge Amt',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'discountPercent',
        headerName: 'Discount %',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => <span>{params.value}%</span>,
      },
      {
        field: 'fourDayCount',
        headerName: 'Four Day Credit Count',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'fourDayAmount',
        headerName: 'Four Day Credit Amt',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{formatMoney(Number(params.value ?? 0))}</span>
        ),
      },
      {
        field: 'groupcount',
        headerName: 'Groups',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'ivrcount',
        headerName: 'IVRs',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'queuescount',
        headerName: 'Queues',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'teamscount',
        headerName: 'Teams',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'powerlistcount',
        headerName: 'PowerLists',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'cadencecount',
        headerName: 'Cadences',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'webhookcount',
        headerName: 'Webhooks',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'smstcount',
        headerName: 'SMSt',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'vmdcount',
        headerName: 'VMD',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'numbercount',
        headerName: 'Numbers',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'blockedCount',
        headerName: 'Blocked',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) =>
          businessSegmentEsDataLoadable?.state === 'loading' ? (
            <CircularProgress size={20} />
          ) : (
            <span>{params.value}</span>
          ),
      },
      {
        field: 'UnallocatedNumbers',
        headerName: 'Unalloc',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'ImageSpamCount',
        headerName: 'Spam',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'NonSMS',
        headerName: 'NonSMS',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'AvgOutgoingPerAgent',
        headerName: 'Avg Outgoing Calls / Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'AvgOutgoingAnsweredPerAgent',
        headerName: 'Avg Outgoing Answered Calls / Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'AvgOutgoingCountOver150PerAgent',
        headerName: '# Outg Calls > 2.5min',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'OutgoingConnectionRateOver150PerAgent',
        headerName: '% Outg Calls > 2.5min',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'OutgoingConnectionUnder30PerAgent',
        headerName: '# Outg Calls < 30sec',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'AvgIncomingPerAgent',
        headerName: '# Inc Calls/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0).toFixed(1)}</span>
        ),
      },
      {
        field: 'IncomingMissedRatePerAgent',
        headerName: '% Inc Missed Calls',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0).toFixed(0)}%</span>
        ),
      },
      {
        field: 'AvgOutgoingPerAgentSMS',
        headerName: 'Outg SMS/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0).toFixed(0)}</span>
        ),
      },
      {
        field: 'AvgOutgoingMMSPerAgentSMS',
        headerName: 'Outg MMS/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0).toFixed(0)}</span>
        ),
      },
      {
        field: 'DncBlocksOutboundSMS',
        headerName: 'Outg Blocked SMS/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
      },
      {
        field: 'AvgIncomingPerAgentSMS',
        headerName: 'Inc SMS/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0)?.toFixed(0) ?? 0}</span>
        ),
      },
      {
        field: 'AvgIncomingMMSPerAgentSMS',
        headerName: 'Inc MMS/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0)?.toFixed(0) ?? 0}</span>
        ),
      },
      {
        field: 'AvgIncomingBlockedPerAgentSMS',
        headerName: 'Inc Blocked SMS/Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => (
          <span>{Number(params.value ?? 0)?.toFixed(0) ?? 0}</span>
        ),
      },
      {
        field: 'contactsCount',
        headerName: 'Contacts',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) =>
          businessSegmentEsDataLoadable?.state === 'loading' ? (
            <CircularProgress size={20} />
          ) : (
            <span>{params.value}</span>
          ),
      },
      {
        field: 'crmStats',
        headerName: 'CRM Stats',
        // headerAlign: 'center',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        // valueFormatter: (params) => {
        //   // Format value for csv download
        //   let str = '';
        //   (params.value ?? ([] as Array))?.forEach((el) => {
        //     str =
        //       str +
        //       'CRM: ' +
        //       getCRMName(el?.crmid) +
        //       ' #contacts: ' +
        //       el?.contactsCount +
        //       ' #users: ' +
        //       el?.usersCount +
        //       ' ';
        //   });
        //   return str;
        // },
        renderCell: (params) =>
          businessSegmentEsDataLoadable?.state === 'loading' ? (
            <CircularProgress size={20} />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
              }}
            >
              {Array.isArray(params.value) &&
                params.value?.map(
                  (
                    stat: {
                      crmid: number;
                      usersCount: number;
                      contactsCount: number;
                    },
                    i
                  ) => {
                    const crmName = getCRMName(stat.crmid);
                    return (
                      <div
                        key={`admin-stats-${i}`}
                        className={`font-size-sm px-2 ${classes.statItemContainer}`}
                        style={{
                          display: 'flex',
                          fontSize: '11px',
                          flexDirection: 'row',
                          alignContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <div className={`${classes.adminBolded}`}>
                          {crmName}
                        </div>{' '}
                        <span
                          style={{
                            cursor: 'pointer',
                          }}
                        >
                          {' '}
                          <Tooltip
                            style={{ cursor: 'pointer' }}
                            // TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            placement="right-start"
                            title={
                              <div>
                                <div>Users: {stat.usersCount}</div>
                                <span>Contacts: {stat.contactsCount}</span>
                              </div>
                            }
                          >
                            <InfoIcon
                              fontSize="inherit"
                              color="primary"
                              style={{
                                marginLeft: '2px',
                              }}
                            />
                          </Tooltip>
                        </span>
                      </div>
                    );
                  }
                )}
            </div>
          ),
      },
      {
        field: 'api',
        headerName: 'API Usage',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        // valueFormatter: (params) => {
        //   // Format value for csv download
        //   let str = '';
        //   if (params.value) {
        //     for (const key in (params as any)?.value) {
        //       if (params.value?.[key] !== null) {
        //         str += key + ': ' + params.value?.[key] + ' ';
        //       }
        //     }
        //   }
        //   return str;
        // },
        renderCell: (params) => {
          if (businessSegmentEsDataLoadable?.state === 'loading') {
            return <CircularProgress size={20} />;
          } else {
            const displayDate = new Date();
            const createdDate = displayDate.toISOString().split('T')[0];
            const isUsageDataForToday =
              (params.value as any)?.createdDate === createdDate;
            const usageDispay =
              isUsageDataForToday === false
                ? '0'
                : ((params.value as any)?.usedRequests > 0
                    ? (params.value as any)?.usedRequests
                    : '0') +
                  ((params.value as any)?.quotaLimit > 0
                    ? '/' + (params.value as any)?.quotaLimit
                    : '') +
                  ((params.value as any)?.percentageUsed > 0
                    ? ' (' +
                      ((params.value as any)?.percentageUsed * 100).toFixed(3) +
                      '%)'
                    : '');
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <div>{usageDispay}</div>
                <div>
                  <PopoverGeneric
                    type="IconButton"
                    iconForButton={<BarChartIcon />}
                    showPopover={true}
                    showPopoverIcon={false}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    onHandleClick={(e) => {
                      setSelectedBid(Number(params.row.id) ?? 0);
                    }}
                    popoverData={
                      <div>
                        <HighchartsReactWithStyles
                          allowChartUpdate={true}
                          highcharts={Highcharts}
                          options={usageReportChartOptions}
                          loading={usageReportDataLoadable?.state === 'loading'}
                        />
                      </div>
                    }
                  />
                </div>
              </div>
            );
          }
        },
      },
    ],
    [
      businessSegmentDataLoadable,
      usageReportData,
      usageReportChartOptions,
      usageReportDataLoadable,
      setSelectedBid,
      businessSegmentEsDataLoadable,
    ]
  );

  const totalCount = useMemo(
    () =>
      businessSegmentDataLoadable.state === 'hasValue'
        ? businessSegmentDataLoadable?.contents?.response?.data?.result?.[0]
            ?.TotalCount
        : 0,
    [businessSegmentDataLoadable]
  );
  const getApiUsage = (eSSegTransformed, el) => {
    const apiUsage =
      eSSegTransformed?.length > 0
        ? eSSegTransformed?.find((e) => Number(e?.id) === Number(el.businessid))
            ?.apiUsage ?? []
        : [];
    return apiUsage;
  };
  const rows = useMemo(
    () =>
      businessSegmentDataLoadable.state === 'hasValue' &&
      businessSegmentDataLoadable?.contents?.response?.data?.result?.length >
        0 &&
      totalCount > 0
        ? businessSegmentDataLoadable?.contents?.response?.data?.result?.map(
            (el) => ({
              id: el.businessid,
              AvgOutgoingPerAgent: el?.AvgOutgoingPerAgent?.toFixed(1) ?? 0,
              AvgOutgoingAnsweredPerAgent:
                el?.AvgOutgoingAnsweredPerAgent?.toFixed(1) ?? 0,
              AvgOutgoingCountOver150PerAgent:
                el?.AvgOutgoingCountOver150PerAgent?.toFixed(1) ?? 0,
              OutgoingConnectionRateOver150PerAgent:
                el?.OutgoingConnectionRateOver150PerAgent?.toFixed(0) ?? 0,
              OutgoingConnectionUnder30PerAgent:
                el?.OutgoingConnectionUnder30PerAgent?.toFixed(1) ?? 0,
              ...el,
              blockedCount:
                eSSegTransformed?.length > 0
                  ? eSSegTransformed?.find(
                      (e) => Number(e?.id) === Number(el.businessid)
                    )?.blockedCount?.count ?? 0
                  : 0,
              contactsCount:
                eSSegTransformed?.length > 0
                  ? eSSegTransformed?.find(
                      (e) => Number(e?.id) === Number(el?.businessid)
                    )?.count?.count ?? 0
                  : 0,
              crmStats:
                eSSegTransformed?.length > 0
                  ? eSSegTransformed?.find(
                      (e) => Number(e?.id) === Number(el.businessid)
                    )?.crmStats ?? []
                  : [],
              api: getApiUsage(eSSegTransformed, el),
            })
          )
        : [],
    [businessSegmentDataLoadable, eSSegTransformed]
  );

  React.useEffect(() => {
    if (
      rows?.length === 1 &&
      values?.autoSwitch === true &&
      userAtom.businessid !== rows[0].businessid
    ) {
      onHandleSwitch(rows[0].businessid, rows[0].apiKey);
    }
  }, [rows]);

  const initialValues = {
    type: 'businessid',
    value: '',
    autoSwitch: false,
  };

  const validationSchema = Yup.object().shape({
    value: Yup.string().required('Please Enter the Value to Search'),
  });
  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setPage(1);
      setValues(values);
      setRefetchToggleForBusinessSegment({});
    },
  });
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <Card
        style={{
          padding: '12px',
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          alignContent: 'center',
        }}
      >
        {/* <div style={{ marginTop: '8px' }}>
          <CSVLink
            filename={`Kixie-BID-${moment(start).format(
              'YYYY-MM-DD'
            )} - ${moment(end).format('YYYY-MM-DD')}`}
            data={props?.csvData ?? ''}
            className={classes.toolbarItem}
          >
            <Button
              className={classes.toolbarButton}
              variant="outlined"
              color="primary"
              type="button"
              startIcon={<GridOnIcon />}
            >
              CSV
            </Button>
          </CSVLink>
        </div> */}
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <Box
            display="flex"
            flexDirection="row"
            // justifyContent="space-between"
          >
            <Box display="flex">
              {businessSegmentDataLoadable?.state === 'loading' ? (
                <Box display="flex" flexDirection="column">
                  <Skeleton
                    className={classes.barSkeleton}
                    variant="rectangular"
                    width={175}
                    height={46}
                  />
                </Box>
              ) : (
                <Box display="flex" flexDirection="column">
                  {formik.values.type === 'accountowner' ? (
                    <div
                      style={{
                        minWidth: '175px',
                      }}
                    >
                      <MaterialAutocomplete
                        type="text"
                        fullWidth
                        label="Account Owner"
                        size="small"
                        variant="outlined"
                        options={
                          users?.sort((a, b) =>
                            a?.fname
                              ?.toLowerCase()
                              ?.localeCompare(b?.fname?.toLowerCase())
                          ) ?? []
                        }
                        value={accountOwner}
                        onChange={(
                          event: React.ChangeEvent<any>,
                          newValue: any,
                          reason: any,
                          details: any
                        ) => {
                          formik.setFieldValue(`value`, newValue?.email);
                          setAccountOwner(newValue);
                          if (newValue === null) {
                            setValues({});
                            setAccountOwner({
                              fname: '',
                              lname: '',
                              email: '',
                            });
                            setRefetchToggleForBusinessSegment({});
                            // setRefetchToggleForBsEsData({})
                          }
                        }}
                        getOptionLabel={(user) =>
                          (user?.fname ?? '') + ' ' + (user?.lname ?? '') ?? ''
                        }
                        isOptionEqualToValue={(user, value) =>
                          user?.userid === value?.userid
                        }
                      />
                    </div>
                  ) : (
                    <>
                      <TextField
                        className={getInputClasses('value')}
                        size="small"
                        label="Search"
                        variant="outlined"
                        {...formik.getFieldProps('value')}
                        name="value"
                      />
                      {formik.submitCount > 0 &&
                        formik.touched.value &&
                        formik.errors.value && (
                          <Typography variant="caption" color="error">
                            {formik.errors.value}
                          </Typography>
                        )}
                    </>
                  )}
                </Box>
              )}

              <FormControl
                className={classes.dropdown}
                size="small"
                variant="outlined"
              >
                <InputLabel>Filter Type</InputLabel>
                <Select
                  label="Active Status"
                  {...formik.getFieldProps('type')}
                  name="type"
                >
                  <MenuItem value={'accountowner'}>Account Owner</MenuItem>
                  <MenuItem value={'businessid'}>Business ID</MenuItem>
                  <MenuItem value={'useremail'}>User Email</MenuItem>
                  <MenuItem value={'number'}>Phone Number</MenuItem>
                  <MenuItem value={'businessname'}>Business Name</MenuItem>
                </Select>
              </FormControl>
              {/* <FormikSelect
                name="autoSwitch"
                className={classes.autoField}
                formik={formik}
                label="Auto-Switch If One"
                size="small"
                variant="outlined"
              >
                <MenuItem value={true}>True</MenuItem>
                <MenuItem value={false}>False</MenuItem>
              </FormikSelect> */}
            </Box>
            <Box>
              <Button
                className={classes.button}
                color="primary"
                variant="outlined"
                disabled={businessSegmentDataLoadable?.state === 'loading'}
                onClick={() => {
                  formik.resetForm({});
                  setAccountOwner({ fname: '', lname: '', email: '' });
                  setValues({});
                  setRefetchToggleForBusinessSegment({});
                  // setRefetchToggleForBsEsData({})
                }}
              >
                Clear
              </Button>
              <Button
                className={classes.button}
                color="primary"
                variant="contained"
                type="submit"
                disabled={businessSegmentDataLoadable?.state === 'loading'}
              >
                Search
              </Button>
            </Box>
          </Box>
        </form>
      </Card>
    ),
    [businessSegmentDataLoadable, formik, initialValues]
  );

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return 'is-invalid';
    }
    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return 'is-valid';
    }
    return '';
  };

  return (
    <div>
      <DataTable
        rows={rows ?? []}
        columns={columns}
        page={page}
        rowCount={totalCount}
        pageSize={pageSize}
        ToolbarComponent={ToolbarComponent}
        paginationMode="server"
        loading={businessSegmentDataLoadable.state === 'loading'}
        pagination
        sortModel={sortModel}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default BIDSearch;
